import React, { useState, useEffect } from 'react';
import { SkillGroupType, UIBadge, UserSkillRatingDocument } from 'types';
import ProfileContentHeader from './ProfileContentHeader';
import { buildStyles, CircularProgressbarWithChildren } from 'react-circular-progressbar';
import GradientSVG from './CircularProgressGradiant';
import { Checkbox } from 'flowbite-react';
import CustomProgressSlider from './CustomProgressSlider';
import classNames from 'classnames';
import { Zoom } from '@mui/material';
import { isScoreRangeValid } from 'shared-utils';
import { getBadgeIcon } from './badges/BadgeCard';

type SliderData = {
	label: string;
	value: number;
	sliderColor: string;
	bgColor: string;
	borderRadius: string;
	filterBy: SkillGroupType;
	darkBgColor: string;
	hide?: boolean;
};

type Props = {
	levelProgressionData: UserSkillRatingDocument[];
	// isSelf?: boolean | null;
	// profileOwnerName?: string;
	showingToCompany: boolean;
	title?: string;
};

export const badgesList: UIBadge[] = [
	{
		title: 'Expert',
		skill: 'Top 1% of users',
		maxScore: 1000,
		minScore: 750,
		scoreRange: 'Top 1% of users',
		bg: '#EDEBFE',
		text: '#5521B5',
		icon: '/assets/badges/expert.svg',
	},
	{
		title: 'Advanced',
		skill: 'Top 10% of users',
		maxScore: 750,
		minScore: 500,
		scoreRange: 'Top 10% of users',
		bg: '#FDF6B2',
		text: '#723B13',
		icon: '/assets/badges/advanced.svg',
	},
	{
		title: 'Intermediate',
		skill: '',
		maxScore: 500,
		minScore: 250,
		scoreRange: 'Top 25% of users',
		bg: '#E1EFFE',
		text: '#1E429F',
		icon: '/assets/badges/intermediate.svg',
	},
	{
		title: 'Beginner',
		skill: '',
		maxScore: 250,
		minScore: 1,
		scoreRange: 'Top 50% of users',
		bg: '#DEF7EC',
		text: '#03543F',
		icon: '/assets/badges/beginner.svg',
	},
];

const getBadgesWithSkills = (levelProgressionData: UserSkillRatingDocument[]) => {
	const badgesWithSkills = badgesList.map(badge => {
		const filteredSkills = (levelProgressionData?.length ? levelProgressionData || [] : []).filter(item => {
			const score = isScoreRangeValid(Math.round(item.rating));
			return score >= badge.minScore && score < badge.maxScore;
		});
		return { ...badge, skills: filteredSkills };
	});

	// Sort badges by the number of skills, descending
	return badgesWithSkills.sort((a, b) => b.minScore - a.minScore)?.filter(el => el.skills.length);
};

export const getSkillColor = (type: SkillGroupType) => {
	switch (type) {
		case SkillGroupType.TECHNICAL_SKILLS:
			return '#5A74F1';
		case SkillGroupType.SOFT_SKILLS:
			return '#FDAC42';
		case SkillGroupType.APTITUDE_SKILLS:
			return '#39D98A';
		case SkillGroupType.THEORETICAL_SKILLS:
			return '#39D98A';
	}
};

const ProfileTechnicalScorecard = ({
	levelProgressionData,
	// isSelf,
	// profileOwnerName,
	title,
	showingToCompany = false,
}: Props) => {
	const [slidersData, setSlidersData] = useState<SliderData[]>([]);
	const [selectedSkillGroupType, setSelectedSkillGroupType] = useState<{ [key in SkillGroupType]: boolean }>({
		[SkillGroupType.TECHNICAL_SKILLS]: true,
		[SkillGroupType.THEORETICAL_SKILLS]: true,
		[SkillGroupType.SOFT_SKILLS]: true,
		[SkillGroupType.APTITUDE_SKILLS]: true,
	});

	const thechnicalSkills = levelProgressionData
		?.filter(a => a.skill.skillGroupType === SkillGroupType.TECHNICAL_SKILLS && a.rating > 0)
		?.sort((a, b) => b.rating - a.rating);
	const theoreticalSkills = levelProgressionData
		?.filter(a => a.skill.skillGroupType === SkillGroupType.THEORETICAL_SKILLS && a.rating > 0)
		?.sort((a, b) => b.rating - a.rating);
	const softSkills = levelProgressionData
		?.filter(a => a.skill.skillGroupType === SkillGroupType.SOFT_SKILLS && a.rating > 0)
		?.sort((a, b) => b.rating - a.rating);
	const aptitudeSkills = levelProgressionData
		?.filter(a => a.skill.skillGroupType === SkillGroupType.APTITUDE_SKILLS && a.rating > 0)
		?.sort((a, b) => b.rating - a.rating);

	const getSliderValue = (skillGroupType: SkillGroupType) => {
		const ratingAvg = Math.round(
			levelProgressionData
				.filter(el => el.skill.skillGroupType === skillGroupType)
				.reduce((acc, el) => acc + el.rating, 0) /
				(levelProgressionData?.filter(el => el.skill.skillGroupType === skillGroupType).length || 1),
		);
		if (ratingAvg >= 0 && ratingAvg <= 200) {
			return 25;
		} else if (ratingAvg > 200 && ratingAvg <= 400) {
			return 50;
		} else if (ratingAvg > 400 && ratingAvg <= 600) {
			return 75;
		} else if (ratingAvg > 600 && ratingAvg <= 1000) {
			return 75;
		}
		return 0;
	};

	const getSliderColor = (type: SkillGroupType) => {
		switch (type) {
			case SkillGroupType.TECHNICAL_SKILLS:
				return 'linear-gradient(90deg, #5A74F1 0%, #57D1FD 100%)';
			case SkillGroupType.SOFT_SKILLS:
				return 'linear-gradient(90deg, #E38202 0%, #FFB48B 100%)';
			case SkillGroupType.APTITUDE_SKILLS:
				return 'linear-gradient(90deg, #0BBE7A 0%, #02F5CD 100%)';
			case SkillGroupType.THEORETICAL_SKILLS:
				return 'linear-gradient(90deg, #0BBE7A 0%, #02F5CD 100%)';
		}
	};

	useEffect(() => {
		setSlidersData([
			{
				label: 'Technical Skills',
				value: getSliderValue(SkillGroupType.TECHNICAL_SKILLS),
				sliderColor: getSliderColor(SkillGroupType.TECHNICAL_SKILLS),
				bgColor: '#EBEBF0',
				borderRadius: '6px',
				filterBy: SkillGroupType.TECHNICAL_SKILLS,
				darkBgColor: '#222428',
				hide: !thechnicalSkills?.length,
			},
			{
				label: 'Soft Skills',
				sliderColor: getSliderColor(SkillGroupType.SOFT_SKILLS),
				bgColor: '#EBEBF0',
				borderRadius: '6px',
				hide: !softSkills?.length,
				filterBy: SkillGroupType.SOFT_SKILLS,
				darkBgColor: '#222428',
				value: getSliderValue(SkillGroupType.SOFT_SKILLS),
			},
			{
				label: 'Aptitude',
				sliderColor: getSliderColor(SkillGroupType.APTITUDE_SKILLS),
				bgColor: '#EBEBF0',
				borderRadius: '6px',
				hide: !aptitudeSkills?.length,
				filterBy: SkillGroupType.APTITUDE_SKILLS,
				darkBgColor: '#222428',
				value: getSliderValue(SkillGroupType.APTITUDE_SKILLS),
			},
			{
				label: 'Theoretical Knowledge',
				sliderColor: getSliderColor(SkillGroupType.THEORETICAL_SKILLS),
				bgColor: '#EBEBF0',
				borderRadius: '6px',
				hide: !theoreticalSkills?.length,
				value: getSliderValue(SkillGroupType.THEORETICAL_SKILLS),
				filterBy: SkillGroupType.THEORETICAL_SKILLS,
				darkBgColor: '#222428',
			},
		]);
	}, [levelProgressionData]);

	const badges = getBadgesWithSkills(levelProgressionData);

	return (
		<div className="flex flex-col gap-2 overflow-hidden rounded-xl dark:bg-[#1F2A37] [&_.CircularProgressbar-trail]:stroke-[5px]">
			<ProfileContentHeader data={{ name: title ?? 'SKILLS', isImp: false }} />

			<div className="p-4 md:p-6">
				<h5 className="text-sm text-[#555770] font-inter font-[400]">
					We evaluate your skills based on the following criteria. View your scores for each category:
				</h5>

				<div>
					<div
						className={classNames('flex-wrap gap-[70px] pt-8 hidden md:flex', showingToCompany ? 'mb-6' : 'mb-[84px]')}
					>
						{slidersData?.length &&
							slidersData
								?.filter(el => !el.hide)
								?.map(el => (
									<div key={el.label} className="flex items-start gap-4">
										<div className="hidden md:block">
											<Checkbox
												color="blue"
												id={el.label}
												checked={selectedSkillGroupType[el.filterBy]}
												onChange={e => {
													setSelectedSkillGroupType(prevState => {
														const updatedState = {
															...prevState,
															[el.filterBy]: !prevState[el.filterBy],
														};

														// Count the number of visible and selected skill groups
														const visibleSelectedCount = slidersData
															.filter(slider => !slider.hide)
															.filter(slider => updatedState[slider.filterBy]).length;

														// If trying to uncheck the last visible selected skill group, prevent the change
														if (visibleSelectedCount === 0) {
															return prevState;
														}

														return updatedState;
													});
												}}
											/>
										</div>
										<label
											htmlFor={el.label}
											className={classNames('w-full', { 'opacity-[0.5]': !selectedSkillGroupType[el.filterBy] })}
										>
											<CustomProgressSlider
												value={el?.value}
												bgColor={el?.bgColor}
												sliderColor={el?.sliderColor}
												borderRadius={el?.borderRadius}
												darkBgColor={el?.darkBgColor}
												maxValue={100}
											/>

											<div className="flex items-center justify-between w-full min-w-[198.152px] mt-2">
												<div className="text-[12px] font-inter font-[600] text-[#555770] dark:text-[#cdced3]">
													{el.label}
												</div>
												<div className="text-xs font-[600] text-[#8F90A6] dark:text-[#c1c1c8]">{el.value}%</div>
											</div>
										</label>
									</div>
								))}
					</div>

					{showingToCompany && badges?.length ? (
						<div className="flex flex-col gap-3 md:gap-6 mt-4">
							{badges.map((badge, index) => (
								<div
									key={index}
									className="w-full flex flex-wrap items-center gap-x-[5px] gap-y-2 justify-start border rounded-[4px] border-[#F2F2F5] px-2 md:px-5 py-[10px] md:py-[15px]"
								>
									<div className="flex items-center gap-3">
										<div className="rounded-full max-w-[40px] md:max-w-[60px] max-h-[40px] md:max-h-[60px]">
											<img src={getBadgeIcon(badge).icon} alt="" />
										</div>
										<div className="flex flex-col gap-[1px] md:gap-2 min-w-[143px]">
											<h4 className="text-base text-[#1C1C28] font-[500]">{badge.title}</h4>
											<p className="text-xs text-[#555770] font-[500]">{badge.scoreRange}</p>
										</div>
									</div>

									<div className="flex flex-wrap gap-2">
										{badge.skills.map((skill, index) => (
											<Zoom
												key={index}
												unmountOnExit
												in={!!selectedSkillGroupType[skill.skill?.skillGroupType as SkillGroupType]}
											>
												<div
													key={index}
													className="border border-[#E7E7EE] flex items-center gap-1 rounded-[18.872px] bg-white font-[500] text-[#555770] text-center text-sm px-2 py-[2px]"
												>
													<span
														className="rounded-full w-[10px] h-[10px]"
														style={{ background: getSkillColor(skill.skill?.skillGroupType as SkillGroupType) }}
													></span>
													<span>{skill.skill?.name}</span>
												</div>
											</Zoom>
										))}
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="px-4 flex-wrap gap-x-[60px] gap-y-[60px] hidden md:flex">
							{thechnicalSkills?.length && selectedSkillGroupType[SkillGroupType.TECHNICAL_SKILLS]
								? thechnicalSkills?.map((data, index) => (
										<Zoom
											key={index}
											in={!!(selectedSkillGroupType[SkillGroupType.TECHNICAL_SKILLS] && thechnicalSkills?.length)}
											unmountOnExit
										>
											<div key={index} className="max-w-[70px]">
												<div className="max-w-[70px] max-h-[67.964px]">
													<GradientSVG
														startColor="#57D1FD"
														endColor="#7777E7"
														idCSS="tech-circle-gradient"
														rotation={0.75}
														gradientTransform="userSpaceOnUse"
														linearGradientOptions={{ x1: 37.1639, y1: 3, x2: 37.1639, y2: 70.9644 }}
													/>
													<CircularProgressbarWithChildren
														styles={buildStyles({
															pathColor: 'url(#tech-circle-gradient)',
															trailColor: '#C1C5D5',
															strokeLinecap: 'round',
															pathTransitionDuration: 1,
															rotation: 0.5,
														})}
														strokeWidth={8}
														circleRatio={1}
														maxValue={1000}
														value={data?.rating}
														className="z-[1] relative"
													>
														<div className="w-full h-full rounded-full overflow-hidden mx-auto flex items-center justify-center">
															<h6 className="text-[19.238px] text-[#797173] font-inter font-[700]">
																{Math.round(data?.rating)}
															</h6>
														</div>
													</CircularProgressbarWithChildren>
												</div>
												<div className="text-[10.581px] text-[#1C1C28] font-inter font-[400] text-center max-w-full mt-[17px] mx-auto">
													{data?.skill?.name}
												</div>
											</div>
										</Zoom>
								  ))
								: null}

							{softSkills?.length && selectedSkillGroupType[SkillGroupType.SOFT_SKILLS]
								? softSkills?.map((data, index) => (
										<Zoom
											key={index}
											in={!!(selectedSkillGroupType[SkillGroupType.SOFT_SKILLS] && softSkills?.length)}
											unmountOnExit
										>
											<div key={index} className="max-w-[70px]">
												<div className="max-w-[70px] max-h-[67.964px]">
													<GradientSVG
														startColor="#FFBD8E"
														endColor="#F63D58"
														idCSS="soft-circle-gradient"
														rotation={0.75}
														gradientTransform="userSpaceOnUse"
														linearGradientOptions={{ x1: 36.9822, y1: 3, x2: 36.9822, y2: 70.9644 }}
													/>
													<CircularProgressbarWithChildren
														styles={buildStyles({
															pathColor: 'url(#soft-circle-gradient)',
															trailColor: '#C1C5D5',
															strokeLinecap: 'round',
															pathTransitionDuration: 1,
															rotation: 0.5,
														})}
														strokeWidth={8}
														circleRatio={1}
														maxValue={1000}
														value={data?.rating}
														className="z-[1] relative"
													>
														<div className="w-full h-full rounded-full overflow-hidden mx-auto flex items-center justify-center">
															<h6 className="text-[19.238px] text-[#797173] font-inter font-[700]">
																{Math.round(data?.rating)}
															</h6>
														</div>
													</CircularProgressbarWithChildren>
												</div>
												<div className="text-[10.581px] text-[#1C1C28] font-inter font-[400] text-center max-w-full mt-[17px] mx-auto">
													{data?.skill?.name}
												</div>
											</div>
										</Zoom>
								  ))
								: null}

							{aptitudeSkills?.length
								? aptitudeSkills?.map((data, index) => (
										<Zoom
											key={index}
											in={!!(selectedSkillGroupType[SkillGroupType.APTITUDE_SKILLS] && aptitudeSkills?.length)}
											unmountOnExit
										>
											<div key={index} className="max-w-[70px]">
												<div className="max-w-[70px] max-h-[67.964px]">
													<GradientSVG
														startColor="#01FBD6"
														endColor="#0ABD78"
														idCSS="aptitude-circle-gradient"
														rotation={0.75}
														gradientTransform="userSpaceOnUse"
														linearGradientOptions={{ x1: 37.1639, y1: 3, x2: 37.1639, y2: 70.9644 }}
													/>
													<CircularProgressbarWithChildren
														styles={buildStyles({
															pathColor: 'url(#aptitude-circle-gradient)',
															trailColor: '#C1C5D5',
															strokeLinecap: 'round',
															pathTransitionDuration: 1,
															rotation: 0.5,
														})}
														strokeWidth={8}
														circleRatio={1}
														maxValue={1000}
														value={data?.rating}
														className="z-[1] relative"
													>
														<div className="w-full h-full rounded-full overflow-hidden mx-auto flex items-center justify-center">
															<h6 className="text-[19.238px] text-[#797173] font-inter font-[700]">
																{Math.round(data?.rating)}
															</h6>
														</div>
													</CircularProgressbarWithChildren>
												</div>
												<div className="text-[10.581px] text-[#1C1C28] font-inter font-[400] text-center max-w-full mt-[17px] mx-auto">
													{data?.skill?.name}
												</div>
											</div>
										</Zoom>
								  ))
								: null}

							{theoreticalSkills?.length
								? theoreticalSkills?.map((data, index) => (
										<Zoom
											key={index}
											in={!!(selectedSkillGroupType[SkillGroupType.THEORETICAL_SKILLS] && theoreticalSkills?.length)}
											unmountOnExit
										>
											<div key={index} className="max-w-[70px]">
												<div className="max-w-[70px] max-h-[67.964px]">
													<GradientSVG
														startColor="#01FBD6"
														endColor="#0ABD78"
														idCSS="aptitude-circle-gradient"
														rotation={0.75}
														gradientTransform="userSpaceOnUse"
														linearGradientOptions={{ x1: 37.1639, y1: 3, x2: 37.1639, y2: 70.9644 }}
													/>
													<CircularProgressbarWithChildren
														styles={buildStyles({
															pathColor: 'url(#aptitude-circle-gradient)',
															trailColor: '#C1C5D5',
															strokeLinecap: 'round',
															pathTransitionDuration: 1,
															rotation: 0.5,
														})}
														strokeWidth={8}
														circleRatio={1}
														maxValue={1000}
														value={data?.rating}
														className="z-[1] relative"
													>
														<div className="w-full h-full rounded-full overflow-hidden mx-auto flex items-center justify-center">
															<h6 className="text-[19.238px] text-[#797173] font-inter font-[700]">
																{Math.round(data?.rating)}
															</h6>
														</div>
													</CircularProgressbarWithChildren>
												</div>
												<div className="text-[10.581px] text-[#1C1C28] font-inter font-[400] text-center max-w-full mt-[17px] mx-auto">
													{data?.skill?.name}
												</div>
											</div>
										</Zoom>
								  ))
								: null}
						</div>
					)}
				</div>

				{showingToCompany ? null : (
					<div className="md:hidden mt-[17px]">
						{[...thechnicalSkills, ...softSkills, ...aptitudeSkills, ...theoreticalSkills].map(el => (
							<div
								key={el._id}
								className="flex items-center justify-between py-1 min-h-[32px] bg-[#F7F9FB] p-3 first-of-type:rounded-t-[2px] last-of-type:rounded-b-[2px] border-b-[0.5px] border-[#8f90a619] last-of-type:border-b-0"
							>
								<div className="w-[100px] text-[12px] font-inter font-[400] text-[#555770]">{el.skill?.name}</div>
								<div className="w-[100px] text-[12px] font-inter font-[400] text-[#555770]">
									<CustomProgressSlider
										height="4px"
										maxValue={1000}
										value={el?.rating}
										bgColor={'inherit'}
										borderRadius={'6px'}
										sliderColor={getSliderColor(el?.skill?.skillGroupType as SkillGroupType)}
									/>
								</div>
								<div className="w-[31.402px] text-right text-[12px] font-inter font-[400] text-[#555770]">
									{Math.round(el?.rating)}
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default ProfileTechnicalScorecard;
